import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { LAZY_ROW_CHANGE } from '../LazyRows'
import { motion, useReducedMotion } from 'framer-motion'
import {
  SELECTION_HULL_DURATION,
  selectionHullDelay,
  selectionHullTransition,
  type SelectionHullCause,
} from '@/src/renderer/lib/motion/selectionHullMotion'
import { hullRuns, planHulls, staggerRanks, type HullEntry, type HullRect, type HullRun } from './hullLayout'
import type { HullStore } from './hullScope'
import { installSelectionInput, selectionInputCause } from './selectionInput'
import './SelectionHull.scss'

interface HullState {
  entries: readonly HullEntry[]
  cause: SelectionHullCause
}

/** Was eine Liste beim letzten Rechnen gemeldet hatte. */
interface SourceSnapshot {
  key: string
  suspended: boolean
  rowIds: readonly string[]
  inIds: ReadonlySet<string>
  excludedId: string | null
}

const EMPTY: ReadonlySet<string> = new Set()
/** So lange nach dem Ausblenden bleibt eine Fläche stehen, falls ihre Zeilen zurückkommen. */
const DYING_MS = SELECTION_HULL_DURATION * 1000 + 80

/**
 * Was von der Zeile zu sehen ist: Eine einfahrende Zeile (`EnteringRow`) steckt in einem Kasten, der
 * von 0 aufgeht und den Rest abschneidet. Die Fläche liegt außerhalb davon und würde sonst schon in
 * voller Höhe über der Zeile darunter stehen.
 */
const CLIPS = /hidden|clip|auto|scroll/

function visibleRect(row: HTMLElement, container: HTMLElement): { top: number; left: number; width: number; height: number } {
  const r = row.getBoundingClientRect()
  let top = r.top
  let left = r.left
  let bottom = r.bottom
  let right = r.right
  for (let el = row.parentElement; el && el !== container; el = el.parentElement) {
    const style = getComputedStyle(el)
    const clipsX = CLIPS.test(style.overflowX || style.overflow)
    const clipsY = CLIPS.test(style.overflowY || style.overflow)
    if (!clipsX && !clipsY) continue
    const clip = el.getBoundingClientRect()
    if (clipsY) {
      top = Math.max(top, clip.top)
      bottom = Math.min(bottom, clip.bottom)
    }
    if (clipsX) {
      left = Math.max(left, clip.left)
      right = Math.min(right, clip.right)
    }
  }
  return { top, left, width: right - left, height: bottom - top }
}

/**
 * Kam nur dazu, was gerade erst in der Liste auftaucht — eine frisch angelegte Aufgabe, die schon
 * markiert ankommt? Bis dahin malte der Ersteller seine Pille selbst; die Fläche steht dann sofort
 * da, statt einzublenden (sonst blinkte die Markierung).
 */
function arrivedSelected(last: SourceSnapshot, rowIds: readonly string[], inIds: ReadonlySet<string>): boolean {
  const before = new Set(last.rowIds)
  const now = new Set(rowIds)
  const added = [...inIds].filter(id => now.has(id) && !last.inIds.has(id))
  const removed = [...last.inIds].filter(id => before.has(id) && !inIds.has(id))
  return added.length > 0 && removed.length === 0 && added.every(id => !before.has(id))
}

/**
 * Ging nur eine Zeile auf oder zu? Aufgehend übernimmt sie ihre Tönung selbst und blendet sie aus
 * (`TaskEntry`, `--hull-open`) — die Fläche dahinter geht sofort, sonst tönte sie doppelt. Zugehend
 * steht die Fläche sofort wieder da, wie früher die Tönung der Zeile (Sven, 09.10.2026: „immens
 * wichtig“).
 */
function onlyOpenedOrClosed(last: SourceSnapshot, inIds: ReadonlySet<string>, excludedId: string | null): boolean {
  const added = [...inIds].filter(id => !last.inIds.has(id))
  const removed = [...last.inIds].filter(id => !inIds.has(id))
  return added.every(id => id === last.excludedId) && removed.every(id => id === excludedId)
}

export interface HullLayerProps {
  store: HullStore
  kind: 'task' | 'subtask'
  /** Alles ruht (ein Abschnitt wird gezogen). */
  resting?: boolean
  layerRef: RefObject<HTMLDivElement | null>
}

/**
 * **Die Ebene der Auswahl-Hülle** (#293): misst die markierten Zeilen aller gemeldeten Listen
 * relativ zu ihrem Elternteil, rechnet die Blöcke **je Liste** (ein Block reicht nie über eine Liste
 * hinaus) und plant alle Flächen zusammen — so wandert eine Fläche auch von Liste zu Liste.
 *
 * **Liegt als erstes Kind in ihrem Kasten** (`position: relative`), absolut und ohne `z-index`. Die
 * Zeilen (`position: relative`) kommen danach im DOM und liegen deshalb darüber. Mit `z-index` oder
 * `isolation` bekäme der Kasten einen eigenen Stapel, und eine aufgeklappte Zeile samt Menüs könnte
 * unter dem verschwinden, was danach kommt.
 *
 * **Gemessen, nicht gerechnet:** neu bei jeder Meldung und über einen `ResizeObserver` (Zeile klappt
 * auf, Fenster wird breiter). Fahren tut die Fläche nur bei einer anderen Auswahl, sonst springt sie.
 * Hat der Kasten keine Größe (Tab hinter `display: none`), misst sie nicht (CLAUDE.md, TabStack).
 */
export const HullLayer: React.FC<HullLayerProps> = ({ store, kind, resting = false, layerRef }) => {
  const reduced = useReducedMotion() ?? false
  const reducedRef = useRef(reduced)
  reducedRef.current = reduced
  const restingRef = useRef(resting)
  restingRef.current = resting

  /*
    **Schlüssel je Ebene, nicht je Modul:** Ein Zähler auf Modulebene fing nach einem Hot-Reload
    wieder bei 1 an, während die Flächen stehen blieben. Doppelte Schlüssel ließen React Flächen
    vertauschen — eine fuhr dann irgendwohin (Dev-App, 09.10.2026).
  */
  const keyCounterRef = useRef(0)
  const makeKey = useCallback(() => `hull-${++keyCounterRef.current}`, [])

  const [state, setState] = useState<HullState>({ entries: [], cause: 'layout' })
  const [version, setVersion] = useState(0)

  const observerRef = useRef<ResizeObserver | null>(null)
  const observedRef = useRef(new Set<Element>())
  const lastRef = useRef(new Map<string, SourceSnapshot>())
  const beforeRef = useRef<ReadonlySet<string>>(EMPTY)
  const delaysRef = useRef(new Map<string, { el: HTMLElement; timer: ReturnType<typeof setTimeout> }>())

  const clearDelay = useCallback((id: string) => {
    const delay = delaysRef.current.get(id)
    if (!delay) return
    clearTimeout(delay.timer)
    delay.el.style.removeProperty('--selection-delay')
    delaysRef.current.delete(id)
  }, [])

  /**
   * `commit`: Eine Liste hat etwas gemeldet — fahren, wenn sich eine Auswahl geändert hat.
   * `resize`: Nur das Layout hat sich bewegt — springen, an den Meldungen ändert sich nichts.
   */
  const measure = useCallback((mode: 'commit' | 'resize') => {
    const container = layerRef.current?.parentElement
    if (!container) return
    const box = container.getBoundingClientRect()
    if (box.width === 0 || box.height === 0) return
    const resting = restingRef.current

    const runs: HullRun[] = []
    const rows = new Map<string, HTMLElement>()
    const snapshots = new Map<string, SourceSnapshot>()
    const after = new Set<string>()
    let selectionChanged = false

    for (const [sourceId, source] of store.sources) {
      const suspended = resting || source.suspended
      const inIds = suspended ? EMPTY : source.inIds
      const key = suspended ? '' : source.selectionKey
      const last = lastRef.current.get(sourceId)
      snapshots.set(sourceId, { key, suspended, rowIds: source.rowIds, inIds, excludedId: source.excludedId })
      // Ein Zug beginnt oder endet: Die Zeilen klappen dabei sofort zu bzw. auf, die Fläche auch.
      if (
        last && last.key !== key && last.suspended === suspended
        && !arrivedSelected(last, source.rowIds, inIds)
        && !onlyOpenedOrClosed(last, inIds, source.excludedId)
      ) {
        selectionChanged = true
      }
      for (const id of inIds) after.add(id)

      const list = source.el?.parentElement
      const rects = new Map<string, HullRect>()
      // `scrollTop`: Liegt die Ebene direkt in einem Scroller, rollt sie mit dessen Inhalt.
      const place = (id: string, row: HTMLElement, r: { top: number; left: number; width: number; height: number }) => {
        if (r.height <= 0 || r.width <= 0) return
        rows.set(id, row)
        rects.set(id, {
          top: r.top - box.top - container.clientTop + container.scrollTop,
          left: r.left - box.left - container.clientLeft + container.scrollLeft,
          width: r.width,
          height: r.height,
        })
      }
      if (source.measureSelf) {
        const id = source.rowIds[0]
        const r = source.el && id !== undefined && inIds.has(id) && !rows.has(id) ? source.measureSelf() : null
        if (r && source.el) place(id, source.el, r)
      } else if (list && source.rowAttribute && inIds.size > 0) {
        const attribute = source.rowAttribute
        for (const el of list.querySelectorAll<HTMLElement>(`[${attribute}]`)) {
          const id = el.getAttribute(attribute)
          if (id === null || !inIds.has(id) || rows.has(id)) continue
          const row = source.rowSelector && !el.matches(source.rowSelector)
            ? el.querySelector<HTMLElement>(source.rowSelector)
            : el
          if (!row) continue
          place(id, row, visibleRect(row, container))
        }
      }
      runs.push(...hullRuns({ rowIds: source.rowIds, inIds, dormantIds: source.dormantIds, rects }))
    }
    runs.sort((a, b) => a.rect.top - b.rect.top || a.rect.left - b.rect.left)

    // Eine markierte Zeile, die ihre Höhe ändert (zweizeilig, Marke dazu), ändert nicht immer den Kasten.
    const observer = observerRef.current
    if (observer) {
      const measured = new Set<Element>(rows.values())
      for (const el of observedRef.current) {
        if (measured.has(el)) continue
        observer.unobserve(el)
        observedRef.current.delete(el)
      }
      for (const el of measured) {
        if (observedRef.current.has(el)) continue
        observer.observe(el)
        observedRef.current.add(el)
      }
    }

    const instant = mode === 'resize' || !selectionChanged || reducedRef.current
    // Per ↑/↓ fährt sie im kurzen Takt, sonst im langen (`selectionInput`).
    const cause: SelectionHullCause = instant ? 'layout' : selectionInputCause()
    setState(prev => {
      const entries = planHulls(prev.entries, runs, { makeKey, instant, now: performance.now() })
      return entries === prev.entries ? prev : { entries, cause }
    })
    if (mode === 'resize') return

    lastRef.current = snapshots
    // Schrift und Haken dazukommender Zeilen ziehen der Reihe nach nach; wer geht, geht sofort im Takt.
    const before = beforeRef.current
    beforeRef.current = after
    for (const id of before) if (!after.has(id)) clearDelay(id)
    if (instant) return
    for (const [sourceId, source] of store.sources) {
      const inIds = snapshots.get(sourceId)!.inIds
      for (const [id, rank] of staggerRanks(source.rowIds, before, inIds)) {
        const el = rows.get(id)
        const ms = selectionHullDelay(rank)
        if (!el || ms === 0) continue
        clearDelay(id)
        el.style.setProperty('--selection-delay', `${ms}ms`)
        delaysRef.current.set(id, { el, timer: setTimeout(() => clearDelay(id), ms + DYING_MS) })
      }
    }
  }, [store, layerRef, clearDelay, makeKey])

  useLayoutEffect(() => store.subscribe(() => setVersion(v => v + 1)), [store])
  useEffect(() => installSelectionInput(), [])

  useLayoutEffect(() => {
    measure('commit')
  }, [version, resting, measure])

  // Eine Zeile wird nachgebaut oder entladen (Lazy Rendering, #303): einmal je Frame nachmessen, nicht je Zeile.
  useEffect(() => {
    const container = layerRef.current?.parentElement
    if (!container) return
    let frame = 0
    const onRowChange = () => {
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; measure('resize') })
    }
    container.addEventListener(LAZY_ROW_CHANGE, onRowChange)
    return () => {
      container.removeEventListener(LAZY_ROW_CHANGE, onRowChange)
      cancelAnimationFrame(frame)
    }
  }, [measure, layerRef])

  useEffect(() => {
    const container = layerRef.current?.parentElement
    if (!container) return
    const observer = new ResizeObserver(() => measure('resize'))
    observerRef.current = observer
    observer.observe(container)
    const observed = observedRef.current
    const delays = delaysRef.current
    return () => {
      observer.disconnect()
      observerRef.current = null
      observed.clear()
      for (const { el, timer } of delays.values()) {
        clearTimeout(timer)
        el.style.removeProperty('--selection-delay')
      }
      delays.clear()
    }
  }, [layerRef, measure])

  // Ausgeblendete Flächen gehen nach der Fahrt — es sei denn, ihre Zeilen kamen zurück.
  const dyingTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  useEffect(() => {
    const timers = dyingTimersRef.current
    const dying = new Set(state.entries.filter(entry => entry.dying).map(entry => entry.key))
    for (const [key, timer] of timers) {
      if (dying.has(key)) continue
      clearTimeout(timer)
      timers.delete(key)
    }
    for (const key of dying) {
      if (timers.has(key)) continue
      timers.set(key, setTimeout(() => {
        timers.delete(key)
        setState(prev => ({ ...prev, entries: prev.entries.filter(entry => !(entry.dying && entry.key === key)) }))
      }, DYING_MS))
    }
  }, [state.entries])

  useEffect(() => {
    const timers = dyingTimersRef.current
    return () => {
      for (const timer of timers.values()) clearTimeout(timer)
      timers.clear()
    }
  }, [])

  const transition = selectionHullTransition(state.cause, reduced)

  return (
    <div ref={layerRef} className={`selection-hull selection-hull--${kind}`} data-hull-cause={state.cause} aria-hidden>
      {state.entries.map(entry => (
        <motion.div
          key={entry.key}
          className={`selection-hull__block${entry.dormant ? ' selection-hull__block--dormant' : ''}`}
          data-hull-ids={entry.ids.join(' ')}
          initial={entry.from
            ? { ...entry.from, opacity: 1 }
            : { ...entry.rect, opacity: state.cause === 'layout' ? 1 : 0 }}
          animate={{ ...entry.rect, opacity: entry.dying ? 0 : 1 }}
          transition={transition}
        />
      ))}
    </div>
  )
}
